import type { HomeDelayedPatient } from "@/api/adapters/home-dashboard";

import { EmptyMessage } from "@/components/feedback/EmptyMessage";
import { Button } from "@/components/ui/Button";
import { useSendSms } from "@/shared/hooks/use-patients-list";
import { maskNameKeepLast } from "../utils";
import { RiskPill } from "@/components/ui/RiskPill";

interface Props {
	data: HomeDelayedPatient[];
	weekStart: string;
	t: (key: string, opts?: Record<string, unknown>) => string;
	navigate: (path: string) => void;
}

export function DelayDetail({
	data: patients,
	weekStart: _weekStart,
	t,
	navigate,
}: Props) {
	const sendSmsMutation = useSendSms();

	if (!patients.length) {
		return (
			<EmptyMessage msg={t("medical:home.sections.responseDelay.empty")} />
		);
	}

	const handleSms = (uid: string, name: string, phone: string) => {
		sendSmsMutation.mutate({ uid, name, phone });
	};

	return (
		<div className="modal-pat-list">
			{patients.map((p) => (
				<div key={p.uid} className="modal-pat modal-pat--missed">
					<div className="modal-pat__main">
						<div className="delay-pat__head">
							<a
								href={`#/patients?selected=${p.uid}&tab=pro`}
								className="delay-pat__name"
								onClick={(e) => {
									e.preventDefault();
									navigate(`/patients?selected=${p.uid}&tab=pro`);
								}}
							>
								<strong>{maskNameKeepLast(p.name)}</strong>
								{p.mrn && <small>({p.mrn})</small>}
							</a>
							<span className="delay-pat__diag-group">
								<span className="delay-pat__diag">{p.primaryDxName}</span>
								{/* SHCT-828 — one pill (`R 0.6 · 저위험`) tinted by the risk level,
								    the same rendering 환자 관리 uses. It replaces a separate 위험도
								    badge plus a `grade-chip--r` whose colour ignored the level.
								    산출 불가(UNKNOWN) keeps its own red state inside the pill. */}
								<RiskPill riskScore={p.riskScore} riskLevel={p.riskLevel} t={t} />
							</span>
							<span className="delay-pat__missed-cnt">
								{t("medical:home.sections.responseDelay.missedCount", {
									count: p.missedItems.length,
								})}
							</span>
						</div>
						<ul className="delay-items">
							{p.missedItems.map((m) => (
								<li key={m.itemId} className="delay-item">
									<span className="delay-item__title">{m.itemName}</span>
									<span className="delay-item__meta">
										<span>{m.cycleLabel}</span>
										<span className="delay-item__sep">|</span>
										<span>
											{t("medical:home.sections.responseDelay.opened", {
												date: m.expectedDate,
											})}
										</span>
										<span className="delay-item__sep">|</span>
										<span>
											{t("medical:home.sections.responseDelay.daysOverdue", {
												days: m.daysOverdue,
											})}
										</span>
									</span>
								</li>
							))}
						</ul>
					</div>
					<div className="modal-pat__actions">
						<a
							href={`#/patients?selected=${p.uid}&tab=pro`}
							className="btn btn--xs btn--secondary"
							onClick={(e) => {
								e.preventDefault();
								navigate(`/patients?selected=${p.uid}&tab=pro`);
							}}
						>
							{t("medical:home.detail.goToPatient")}
						</a>
						<Button
							variant="secondary"
							className="delay-btn--sms"
							onClick={() => handleSms(p.uid, p.name, p.phone)}
						>
							{t("medical:home.sections.responseDelay.sendSms")}
						</Button>
					</div>
				</div>
			))}
		</div>
	);
}
